import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { GoogleMap, Polyline } from '@react-google-maps/api';
import { getDirections } from '../services/directionsService';
import comms from './comms';

Modal.setAppElement('#root');

const containerStyle = { width: '100%', height: '100%' };

// ── helpers ──────────────────────────────────────────────────────────────────

// Read the first present value among several possible key spellings.
function pick(obj, ...keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return '';
}

function fmtTime(value) {
  if (!value) return 'N/A';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? 'N/A'
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fmtDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// Convert an ISO/date string to the value a <input type="datetime-local"> wants.
function toLocalInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

function statusLabel(status) {
  if (!status) return '';
  return String(status).replace('Driver', '').replace('Vehicle', '').trim();
}

const addr = (trip, lineKey, l1, l2) =>
  pick(trip, lineKey) || `${pick(trip, l1)} ${pick(trip, l2)}`.trim();

// Small stat tile in the read view.
function Stat({ label, value }) {
  if (value === '' || value === null || value === undefined) return null;
  return (
    <div className="rounded-2xl bg-white p-3 shadow-sm">
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-gray-800">{value}</div>
    </div>
  );
}

// A labelled input used in edit mode.
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

const TripModal = ({ trip, onClose, fleetSettings, onSave }) => {
  const [polylinePath, setPolylinePath] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Seed / reset the edit form whenever the selected trip changes.
  useEffect(() => {
    setEditing(false);
    setSaving(false);
    setSaveError(null);
    if (!trip) {
      setForm({});
      return;
    }
    setForm({
      passengerName: pick(trip, 'PassengerName'),
      passengerPhone: pick(trip, 'PassengerPhone', 'passengerPhone', 'Phone', 'phone', 'PhoneNumber', 'phoneNumber', 'Mobile', 'MobilePhone', 'ContactPhone', 'ContactNumber'),
      pickupTime: toLocalInput(trip.OriginScheduledDate),
      dropoffTime: toLocalInput(trip.DestinationScheduledDate),
      pickupAddress: addr(trip, 'startAddress', 'Origin_AddressLine1', 'Origin_AddressLine2'),
      dropoffAddress: addr(trip, 'endAddress', 'Destination_AddressLine1', 'Destination_AddressLine2'),
      ambulatory: trip.AmbulatoryRequired ?? '',
      wheelchair: trip.WheelchairRequired ?? '',
      notes: pick(trip, 'Notes', 'Note', 'Comments', 'DriverNotes', 'TripNotes'),
    });
  }, [trip]);

  // Build the route polyline for the map (hook kept unconditional).
  useEffect(() => {
    if (!trip) {
      setPolylinePath([]);
      return;
    }
    const origin = { lat: trip.OriginLatitude, lng: trip.OriginLongitude };
    const destination = { lat: trip.DestinationLatitude, lng: trip.DestinationLongitude };
    const osrmUrl =
      fleetSettings?.osrmUrl || fleetSettings?.osrmBaseUrl || fleetSettings?.OsrmBaseUrl || null;
    let cancelled = false;
    getDirections({ origin, destination, osrmUrl })
      .then((result) => {
        if (cancelled) return;
        setPolylinePath(result && Array.isArray(result.path) && result.path.length > 0 ? result.path : []);
      })
      .catch(() => {
        if (!cancelled) setPolylinePath([]);
      });
    return () => {
      cancelled = true;
    };
  }, [trip, fleetSettings]);

  if (!trip) return null;

  const hasCoords = Number.isFinite(trip.OriginLatitude) && Number.isFinite(trip.OriginLongitude);
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const changes = {
        Id: trip.Id,
        TripNo: trip.TripNo,
        PassengerName: form.passengerName,
        PassengerPhone: form.passengerPhone,
        OriginScheduledDate: form.pickupTime ? new Date(form.pickupTime).toISOString() : trip.OriginScheduledDate,
        DestinationScheduledDate: form.dropoffTime
          ? new Date(form.dropoffTime).toISOString()
          : trip.DestinationScheduledDate,
        Origin_AddressLine1: form.pickupAddress,
        Destination_AddressLine1: form.dropoffAddress,
        AmbulatoryRequired: form.ambulatory === '' ? trip.AmbulatoryRequired : Number(form.ambulatory),
        WheelchairRequired: form.wheelchair === '' ? trip.WheelchairRequired : Number(form.wheelchair),
        Notes: form.notes,
      };
      await comms.updateTrip(trip.Id, changes);
      const updated = { ...trip, ...changes, startAddress: form.pickupAddress, endAddress: form.dropoffAddress };
      if (onSave) onSave(updated);
      setEditing(false);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const passengerName = pick(trip, 'PassengerName') || 'Passenger';
  const phone = pick(trip, 'PassengerPhone', 'passengerPhone', 'Phone', 'phone', 'PhoneNumber', 'phoneNumber', 'Mobile', 'MobilePhone', 'ContactPhone', 'ContactNumber');
  const pickupAddress = addr(trip, 'startAddress', 'Origin_AddressLine1', 'Origin_AddressLine2');
  const dropoffAddress = addr(trip, 'endAddress', 'Destination_AddressLine1', 'Destination_AddressLine2');
  const fare = pick(trip, 'Fare', 'Amount', 'Total', 'TripFare');
  const distance = pick(trip, 'Miles', 'Distance', 'TripMiles', 'EstimatedMiles');
  const spaceType = pick(trip, 'SpaceType', 'ServiceLevel', 'TripType', 'Type');
  const status = statusLabel(trip.TripStatus);
  const notes = pick(trip, 'Notes', 'Note', 'Comments', 'DriverNotes', 'TripNotes');

  return (
    <Modal
      isOpen={!!trip}
      onRequestClose={onClose}
      contentLabel="Booking Details"
      style={{
        overlay: { zIndex: 2000, backgroundColor: 'rgba(15,23,42,0.55)' },
        content: {
          inset: 0,
          padding: 0,
          border: 'none',
          borderRadius: 0,
          background: '#f8fafc',
          overflow: 'hidden',
        },
      }}
    >
      <div className="flex h-full flex-col bg-slate-50">
        {/* Header — fixed bar above the app's nav (zIndex 2000) */}
        <header className="flex items-center gap-3 bg-[#3f51b5] px-4 py-3 text-white shadow-md">
          <button
            onClick={onClose}
            aria-label="Close"
            className="-ml-1 flex h-9 w-9 items-center justify-center rounded-full text-xl hover:bg-white/15"
          >
            &#8592;
          </button>
          <div className="flex-1">
            <div className="text-base font-semibold leading-tight">Trip #{trip.TripNo || '—'}</div>
            <div className="text-xs text-white/70">{editing ? 'Editing booking' : 'Booking details'}</div>
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="rounded-lg bg-white px-3.5 py-1.5 text-sm font-semibold text-[#3f51b5] shadow-sm hover:bg-blue-50"
            >
              Edit
            </button>
          )}
        </header>

        {/* Body */}
        <div className="flex-1 space-y-4 overflow-auto p-4">
          {editing ? (
            <div className="space-y-4">
              <Field label="Passenger Name">
                <input className={inputCls} value={form.passengerName} onChange={setField('passengerName')} />
              </Field>
              <Field label="Passenger Phone">
                <input
                  className={inputCls}
                  type="tel"
                  value={form.passengerPhone}
                  onChange={setField('passengerPhone')}
                  placeholder="(000) 000-0000"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Pickup Time">
                  <input className={inputCls} type="datetime-local" value={form.pickupTime} onChange={setField('pickupTime')} />
                </Field>
                <Field label="Dropoff Time">
                  <input className={inputCls} type="datetime-local" value={form.dropoffTime} onChange={setField('dropoffTime')} />
                </Field>
              </div>
              <Field label="Pickup Address">
                <input className={inputCls} value={form.pickupAddress} onChange={setField('pickupAddress')} />
              </Field>
              <Field label="Dropoff Address">
                <input className={inputCls} value={form.dropoffAddress} onChange={setField('dropoffAddress')} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ambulatory Seats">
                  <input className={inputCls} type="number" min="0" value={form.ambulatory} onChange={setField('ambulatory')} />
                </Field>
                <Field label="Wheelchair Seats">
                  <input className={inputCls} type="number" min="0" value={form.wheelchair} onChange={setField('wheelchair')} />
                </Field>
              </div>
              <Field label="Notes">
                <textarea
                  className={inputCls}
                  rows={3}
                  value={form.notes}
                  onChange={setField('notes')}
                  placeholder="Add a note for this booking…"
                />
              </Field>
            </div>
          ) : (
            <>
              {/* Passenger + status */}
              <div className="rounded-2xl bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-lg font-semibold text-gray-900">{passengerName}</div>
                    {phone ? (
                      <a href={`tel:${phone}`} className="text-sm font-medium text-blue-600">
                        {phone}
                      </a>
                    ) : (
                      <div className="text-sm text-gray-400">No phone on file</div>
                    )}
                  </div>
                  {status && (
                    <span className="shrink-0 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                      {status}
                    </span>
                  )}
                </div>
              </div>

              {/* Route timeline */}
              <div className="rounded-2xl bg-white p-4 shadow-sm">
                <div className="flex gap-3">
                  <div className="flex flex-col items-center pt-1.5">
                    <span className="h-3 w-3 rounded-full bg-blue-600" />
                    <span className="my-1 w-px flex-1 bg-gray-300" />
                    <span className="h-3 w-3 rounded-full bg-red-500" />
                  </div>
                  <div className="flex-1 space-y-4">
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Pickup</div>
                      <div className="font-medium text-gray-900">
                        {fmtTime(trip.OriginScheduledDate)}
                        {fmtDate(trip.OriginScheduledDate) && (
                          <span className="text-gray-400"> · {fmtDate(trip.OriginScheduledDate)}</span>
                        )}
                      </div>
                      {pickupAddress && <div className="text-sm text-gray-600">{pickupAddress}</div>}
                    </div>
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Dropoff</div>
                      <div className="font-medium text-gray-900">
                        {fmtTime(trip.DestinationScheduledDate)}
                        {fmtDate(trip.DestinationScheduledDate) && (
                          <span className="text-gray-400"> · {fmtDate(trip.DestinationScheduledDate)}</span>
                        )}
                      </div>
                      {dropoffAddress && <div className="text-sm text-gray-600">{dropoffAddress}</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <Stat
                  label="Seats"
                  value={`Amb ${trip.AmbulatoryRequired ?? 0} · WC ${trip.WheelchairRequired ?? 0}`}
                />
                {spaceType && <Stat label="Service" value={spaceType} />}
                {fare !== '' && <Stat label="Fare" value={`$${Number(fare).toFixed(2)}`} />}
                {distance !== '' && <Stat label="Distance" value={`${distance} mi`} />}
              </div>

              {/* Notes */}
              {notes && (
                <div className="rounded-2xl bg-white p-4 shadow-sm">
                  <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Notes</div>
                  <div className="text-sm text-gray-700">{notes}</div>
                </div>
              )}

              {/* Map (only when the trip has coordinates) */}
              {hasCoords && (
                <div className="h-56 overflow-hidden rounded-2xl shadow-sm">
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    options={{
                      disableDefaultUI: true,
                      zoomControl: true,
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: false,
                    }}
                    center={{ lat: trip.OriginLatitude, lng: trip.OriginLongitude }}
                    zoom={12}
                  >
                    {polylinePath.length > 0 && (
                      <Polyline
                        path={polylinePath}
                        options={{ strokeColor: '#0066cc', strokeOpacity: 0.8, strokeWeight: 6 }}
                      />
                    )}
                  </GoogleMap>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer (edit mode only) */}
        {editing && (
          <footer className="flex items-center gap-3 border-t border-gray-200 bg-white px-4 py-3">
            {saveError && <span className="flex-1 text-xs text-red-600">{saveError}</span>}
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => {
                  setEditing(false);
                  setSaveError(null);
                }}
                disabled={saving}
                className="rounded-xl bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-xl bg-[#3f51b5] px-5 py-2 text-sm font-semibold text-white hover:bg-[#36459c] disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </footer>
        )}
      </div>
    </Modal>
  );
};

export default TripModal;
